import { Box, Inline, Notice, Stack, Text } from 'braid-design-system';

import { StatusCodePicker, useStatusCodeState } from './StatusCodePicker.tsx';
import { SubscriptionReplay } from './SubscriptionReplay.tsx';
import { GET_WEBHOOK_SUBSCRIPTION_REPLAYS } from './queries.ts';

import { InfiniteScrollingList } from '#src/components/InfiniteScrollingList/InfiniteScrollingList.tsx';
import {
  PaginationDirectionPicker,
  usePaginationDirectionState,
} from '#src/components/PaginationDirectionPicker/PaginationDirectionPicker.tsx';
import { Query } from '#src/components/Query/Query.tsx';
import type {
  WebhookSubscriptionReplaysQuery,
  WebhookSubscriptionReplaysQueryVariables,
} from '#src/types/generated/graphql.ts';

interface Props {
  subscriptionId: string;
}

export const SubscriptionReplayList = ({ subscriptionId }: Props) => {
  const paginationDirectionState = usePaginationDirectionState();
  const statusCodeState = useStatusCodeState();

  const { firstOrLast, processPageInfo } = paginationDirectionState;
  const { statusCode } = statusCodeState;

  return (
    <Stack space="none">
      <Box padding="medium">
        <Inline align="right" alignY="center" space="medium">
          <StatusCodePicker {...statusCodeState} />

          <PaginationDirectionPicker {...paginationDirectionState} />
        </Inline>
      </Box>

      <Query<
        WebhookSubscriptionReplaysQuery,
        WebhookSubscriptionReplaysQueryVariables
      >
        errorMessage="We couldn’t load your subscription’s replays."
        query={GET_WEBHOOK_SUBSCRIPTION_REPLAYS}
        variables={{
          [firstOrLast]: 10,
          statusCode: statusCode === 'All' ? undefined : statusCode,
          subscriptionId,
        }}
        notifyOnNetworkStatusChange={false}
      >
        {({ data, fetchMore }) => {
          if (!data.webhookSubscription) {
            return (
              <Notice tone="critical">
                <Text>We couldn’t find this subscription.</Text>
              </Notice>
            );
          }

          const {
            webhookSubscriptionReplays: { edges, pageInfo },
          } = data.webhookSubscription;

          const { shouldLoadMore, variables } = processPageInfo(pageInfo);

          const loadMore = () => fetchMore({ variables });

          return (
            <InfiniteScrollingList
              loadMore={loadMore}
              shouldLoadMore={shouldLoadMore}
              isEmpty={edges.length === 0}
              subject="webhook replays"
              items={edges.map(({ node }) => (
                <SubscriptionReplay
                  subscriptionReplay={node}
                  key={node.id.value}
                />
              ))}
            />
          );
        }}
      </Query>
    </Stack>
  );
};
